/**
 * Per-day validity masking for group dashboard data.
 *
 * The backend is inconsistent about how it reports a night with no data: some
 * metrics come back as `null`, others as `0`. A literal `0` plots as a real
 * data point (a spline dropping to the floor, a "0h 0m" tooltip), which reads
 * as "the resident was never in bed" rather than "the sensor has no data".
 *
 * The rule, applied once where the response lands so every chart and summary
 * tile agrees:
 *
 * 1. A day has no data when every presence signal for it is null or 0.
 *    Time in bed is the primary signal, total sleep time the fallback.
 * 2. On a no-data day every metric is null — counts included, because
 *    "0 bed exits" on a night that was never measured is not a measurement.
 * 3. On a day that does have data, 0 is still missing for metrics where 0 is
 *    physiologically impossible (heart rate, breathing rate, HRV, durations,
 *    regularity indices), but a genuine reading for counts (bed exits,
 *    toss & turns, PIR activity).
 */

/** A per-day metric series: one slot per entry in `datetime_scale`. */
export type DayMetric = Array<number | null>;

/**
 * Builds a per-day mask where `true` means the day has data.
 *
 * Pass every array that signals presence — time in bed first, total sleep time
 * as a fallback for feeds that omit it. A day counts as measured when any of
 * them holds a number greater than 0; arrays shorter than the longest one are
 * treated as null past their end.
 */
export function buildDayValidityMask(
  ...presenceSignals: Array<readonly (number | null)[] | undefined>
): boolean[] {
  const signals = presenceSignals.filter(
    (s): s is readonly (number | null)[] => Array.isArray(s)
  );
  const days = signals.reduce((max, s) => Math.max(max, s.length), 0);

  const mask: boolean[] = [];
  for (let i = 0; i < days; i++) {
    mask.push(
      signals.some((s) => {
        const v = s[i];
        return typeof v === 'number' && v > 0;
      })
    );
  }
  return mask;
}

export interface MaskMetricOptions {
  /**
   * Whether a `0` on a day that *does* have data means "missing".
   *
   * `true` (default) for metrics that can never legitimately read 0 — heart
   * rate, breathing rate, HRV, durations, SRI/BTRI. `false` for counts, where
   * 0 is a real measurement.
   */
  zeroIsMissing?: boolean;
}

/**
 * Applies a validity mask to one metric series, returning a new array.
 *
 * Days outside the mask's range are left untouched, so a metric that is longer
 * than the presence signal never silently loses its tail.
 */
export function maskMetric<T extends readonly (number | null)[]>(
  data: T | undefined,
  mask: readonly boolean[],
  { zeroIsMissing = true }: MaskMetricOptions = {}
): DayMetric {
  if (!Array.isArray(data)) return [];

  return (data as readonly (number | null)[]).map((value, i) => {
    if (i < mask.length && !mask[i]) return null;
    if (zeroIsMissing && value === 0) return null;
    return value;
  });
}
